/**
 * Client-side mobility ROM measurement (v3.2 — framing-robust + visibility-aware).
 *
 * Angles are measured FROM VERTICAL / from a midline so they survive partial
 * framing and never depend on a hallucinated, out-of-frame landmark:
 *   • segment_vertical : thigh (hip→knee) or shin (knee→ankle) from vertical
 *   • trunk_vertical   : shoulder-midline→hip-midline from vertical (side bend / fold)
 *   • head_vertical    : shoulder-midline→nose from vertical (neck tilt)
 *   • <3-index triplet> : classic joint angle (shoulder kept as-is)
 *
 * Every landmark used is visibility-checked (`vpt`): a point below the floor is
 * treated as NOT SEEN, so an invented occluded joint can't poison the reading.
 * Midline metrics fall back to whichever side is visible (the far one is often
 * occluded in side view), so they still work when only one shoulder/hip shows.
 */

const SMOOTH_WINDOW = 5;
const HELD_WINDOW = 3;
const MIN_MOVEMENT_DEG = 10;
const VIS_MIN = 0.45;        // a landmark below this is treated as "not really seen"

// ── geometry ──────────────────────────────────────────────────────────────
export const angle2D = (a, b, c) => {
  if (!a || !b || !c) return null;
  const v1x = a.x - b.x, v1y = a.y - b.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (m1 < 1e-6 || m2 < 1e-6) return null;
  const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
};

// Angle of a segment (a→b) away from vertical — direction-agnostic magnitude.
export const angleFromVertical = (a, b) => {
  if (!a || !b) return null;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return null;
  return (Math.atan2(Math.abs(dx), Math.abs(dy)) * 180) / Math.PI;
};

const median = (arr) => {
  const s = arr.filter((x) => x != null).sort((x, y) => x - y);
  const n = s.length;
  if (!n) return null;
  return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
};

// A landmark, but only if it's confidently visible — else null ("not seen").
const vpt = (lm, idx, min = VIS_MIN) => {
  const p = lm?.[idx];
  if (!p) return null;
  if ((p.visibility ?? 1) < min) return null;
  return p;
};

// Average of the visible points (ignores not-seen ones). null if none visible.
const avgPts = (pts) => {
  const v = pts.filter(Boolean);
  if (!v.length) return null;
  return {
    x: v.reduce((s, p) => s + p.x, 0) / v.length,
    y: v.reduce((s, p) => s + p.y, 0) / v.length,
  };
};

export const midpoint = (a, b) => avgPts([a, b]);

// The single source of truth for "what angle does this metric read right now".
// Returns null whenever the landmarks needed aren't confidently visible.
export const metricAngle = (lm, metric, triplet) => {
  if (!lm) return null;
  const mt = metric?.metric_type;
  if (mt === 'trunk_vertical') {
    const top = avgPts([vpt(lm, 11), vpt(lm, 12)]);
    const bot = avgPts([vpt(lm, 23), vpt(lm, 24)]);
    return angleFromVertical(bot, top);
  }
  if (mt === 'head_vertical') {
    const top = avgPts([vpt(lm, 11), vpt(lm, 12)]);
    const nose = vpt(lm, 0);
    return angleFromVertical(top, nose);
  }
  if (triplet && triplet.length === 2) {
    return angleFromVertical(vpt(lm, triplet[0]), vpt(lm, triplet[1]));
  }
  if (triplet && triplet.length === 3) {
    return angle2D(vpt(lm, triplet[0]), vpt(lm, triplet[1]), vpt(lm, triplet[2]));
  }
  return null;
};

// Is this metric measurable on this frame (all needed landmarks visible)?
export const metricVisible = (lm, metric, triplet) => metricAngle(lm, metric, triplet) != null;

// ── series helpers ──────────────────────────────────────────────────────────
const seriesForMetric = (frames, metric, triplet) =>
  frames.map((f) => (f.landmarks2D ? metricAngle(f.landmarks2D, metric, triplet) : null));

// Raw triplet series (no visibility gate) — used only by the compensation guard.
const seriesForTriplet = (frames, triplet) =>
  frames.map((f) => {
    const lm = f.landmarks2D;
    if (!lm) return null;
    if (triplet.length === 2) return angleFromVertical(lm[triplet[0]], lm[triplet[1]]);
    return angle2D(lm[triplet[0]], lm[triplet[1]], lm[triplet[2]]);
  });

const rollingMedian = (series, win = SMOOTH_WINDOW) => {
  const n = series.length;
  const out = new Array(n).fill(null);
  const half = Math.floor(win / 2);
  for (let i = 0; i < n; i++) {
    const w = [];
    for (let j = Math.max(0, i - half); j < Math.min(n, i + half + 1); j++) {
      if (series[j] != null) w.push(series[j]);
    }
    if (w.length) out[i] = median(w);
  }
  return out;
};

const heldExtreme = (smoothed, phase, win = HELD_WINDOW) => {
  const pts = [];
  smoothed.forEach((v, i) => { if (v != null) pts.push([i, v]); });
  if (!pts.length) return { value: null, index: null };
  if (pts.length < win) {
    const pick = phase === 'peak'
      ? pts.reduce((m, p) => (p[1] > m[1] ? p : m))
      : pts.reduce((m, p) => (p[1] < m[1] ? p : m));
    return { value: pick[1], index: pick[0] };
  }
  let bestVal = null, bestIdx = null;
  for (let s = 0; s <= pts.length - win; s++) {
    const window = [];
    for (let k = 0; k < win; k++) window.push(pts[s + k][1]);
    const wv = median(window);
    const centerIdx = pts[s + Math.floor(win / 2)][0];
    if (bestVal == null || (phase === 'peak' && wv > bestVal) || (phase === 'min' && wv < bestVal)) {
      bestVal = wv; bestIdx = centerIdx;
    }
  }
  return { value: bestVal, index: bestIdx };
};

const guardCheck = (frames, guardTriplet, validRange, peakIdx) => {
  const gsm = rollingMedian(seriesForTriplet(frames, guardTriplet));
  const lo = Number(validRange[0]), hi = Number(validRange[1]);
  const vals = [];
  for (let j = Math.max(0, peakIdx - 1); j < Math.min(gsm.length, peakIdx + 2); j++) {
    if (gsm[j] != null) vals.push(gsm[j]);
  }
  if (!vals.length) return { violated: false, peakViolation: null };
  let worst = 0;
  vals.forEach((gv) => {
    const over = gv < lo ? lo - gv : gv > hi ? gv - hi : 0;
    worst = Math.max(worst, over);
  });
  return { violated: worst > 0, peakViolation: worst > 0 ? Math.round(worst * 10) / 10 : null };
};

/**
 * frames + test + side → measurement JSON for /score-measurement.
 * opts.neutral — captured resting angle (rom mode)
 * opts.triplet — the chosen side's triplet (auto-side tests); falls back to L/R.
 */
export const computeMobilityMeasurement = (frames, test, side, opts = {}) => {
  const pm = test.primary_metric || {};
  const phase = pm.phase || 'peak';
  const mode = pm.mode;
  const neutral = opts.neutral;
  const primaryTrip = opts.triplet || (side === 'R' ? pm.triplet_R : pm.triplet_L);

  const series = seriesForMetric(frames, pm, primaryTrip);
  const smoothed = rollingMedian(series);
  const { value: extremeAngle, index: peakIdx } = heldExtreme(smoothed, phase);

  let rawValue = extremeAngle;
  if (mode === 'rom_from_neutral' && neutral != null && extremeAngle != null) {
    const dir = phase === 'peak' ? 1 : -1;
    rawValue = Math.max(0, dir * (extremeAngle - neutral));
  }

  const clean = smoothed.filter((v) => v != null);
  const movementRange = clean.length >= 2 ? Math.max(...clean) - Math.min(...clean) : 0;

  let guardViolated = false;
  let guardPeakViolation = null;
  const guard = test.compensation_guard;
  if (guard && guard.triplet_L && peakIdx != null) {
    const gtrip = side === 'R' ? guard.triplet_R : guard.triplet_L;
    const g = guardCheck(frames, gtrip, guard.valid_range, peakIdx);
    guardViolated = g.violated;
    guardPeakViolation = g.peakViolation;
  }

  const withPose = frames.filter((f) => f.landmarks2D).length;
  return {
    raw_value: rawValue != null ? Math.round(rawValue * 10) / 10 : null,
    neutral_value: neutral != null ? Math.round(neutral * 10) / 10 : null,
    peak_value: extremeAngle != null ? Math.round(extremeAngle * 10) / 10 : null,
    guard_violated: guardViolated,
    guard_peak_violation: guardPeakViolation,
    movement_range_deg: Math.round(movementRange * 10) / 10,
    low_movement: movementRange < MIN_MOVEMENT_DEG,
    frames_analyzed: frames.length,
    frames_with_pose: withPose,
  };
};

export default computeMobilityMeasurement;
